/**
 * Subscription detail — superadmin red panel (Phase 3).
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { superadminAPI } from '../../services/api'
import {
  HiOutlineArrowLeft,
  HiOutlineClock,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlinePencil,
  HiOutlineArrowPath,
  HiOutlineCurrencyDollar,
} from 'react-icons/hi2'

export default function SubscriptionDetailPage() {
  const { subscriptionId } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [actionInFlight, setActionInFlight] = useState(null)

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const res = await superadminAPI.getSubscription(subscriptionId)
      setData(res?.data?.data ?? res?.data ?? null)
    } catch (e) {
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [subscriptionId])

  useEffect(() => { reload() }, [reload])

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  const sub = data.subscription
  const events = data.events || []
  const stripeEvents = data.stripe_events || []

  const handleAction = async (label, fn) => {
    setActionInFlight(label)
    try {
      await fn()
      await reload()
    } catch (e) {
      alert('Action failed: ' + (e?.message || 'unknown'))
    } finally {
      setActionInFlight(null)
    }
  }

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate(-1)}
        className="text-gray-600 hover:text-gray-900 inline-flex items-center gap-1 text-sm"
      >
        <HiOutlineArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{sub.account.name}</h1>
            <p className="text-sm text-gray-500 mt-1">
              Owner: {sub.account.owner_email} (id {sub.account.owner_id})
            </p>
          </div>
          <span className={`text-xs font-bold uppercase px-3 py-1 rounded-full ${
            sub.status === 'active' ? 'bg-green-100 text-green-800' :
            sub.status === 'trial' ? 'bg-info-soft text-info' :
            sub.status === 'past_due' ? 'bg-amber-100 text-amber-800' :
            sub.status === 'blocked' ? 'bg-red-100 text-red-800' :
            sub.status === 'grandfathered' ? 'bg-indigo-100 text-indigo-800' :
            'bg-gray-100 text-gray-700'
          }`}>
            {sub.status}
          </span>
        </div>

        <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <Field label="Plan" value={sub.plan?.name || '—'} />
          <Field label="Price" value={`$${sub.current_price_usd}/yr`} />
          {sub.trial_ends_at && <Field label="Trial ends" value={new Date(sub.trial_ends_at).toLocaleDateString()} />}
          {sub.current_period_end && <Field label="Renews" value={new Date(sub.current_period_end).toLocaleDateString()} />}
          {sub.grace_period_ends_at && <Field label="Grace ends" value={new Date(sub.grace_period_ends_at).toLocaleDateString()} />}
          {sub.next_renewal_price_usd && <Field label="Next renewal" value={`$${sub.next_renewal_price_usd}`} highlight />}
          {sub.stripe_subscription_id && <Field label="Stripe sub" value={sub.stripe_subscription_id.slice(0, 16) + '…'} mono />}
          {sub.stripe_customer_id && <Field label="Stripe customer" value={sub.stripe_customer_id.slice(0, 16) + '…'} mono />}
          <Field label="Storage backend" value={sub.account.storage_backend_override || 'default'} />
          <Field label="Auto failover" value={sub.account.auto_failover_enabled ? 'on' : 'off'} />
        </dl>

        {/* Actions */}
        <div className="mt-6 flex flex-wrap gap-2 border-t border-gray-100 pt-4">
          <ActionButton
            label="Extend trial 30d"
            icon={HiOutlineClock}
            onClick={() => handleAction('extend', () => superadminAPI.extendTrial(sub.id, 30))}
            disabled={actionInFlight === 'extend'}
          />
          {!sub.is_blocked && (
            <ActionButton
              label="Force block"
              icon={HiOutlineXCircle}
              danger
              onClick={() => {
                if (window.confirm('Block this subscription immediately?')) {
                  handleAction('block', () => superadminAPI.forceBlockSubscription(sub.id))
                }
              }}
              disabled={actionInFlight === 'block'}
            />
          )}
          {(sub.is_blocked || sub.status === 'past_due') && (
            <ActionButton
              label="Unblock"
              icon={HiOutlineCheckCircle}
              onClick={() => handleAction('unblock', () => superadminAPI.unblockSubscription(sub.id, 'admin_unblock'))}
              disabled={actionInFlight === 'unblock'}
            />
          )}
          {sub.stripe_subscription_id && (
            <ActionButton
              label="Issue refund"
              icon={HiOutlineCurrencyDollar}
              onClick={() => {
                const amt = window.prompt('Refund amount in USD (blank for full refund):')
                if (amt === null) return
                const reason = window.prompt('Reason:') || 'admin_refund'
                handleAction('refund', () => superadminAPI.refundSubscription(sub.id, {
                  amount_usd: amt || null,
                  reason,
                }))
              }}
              disabled={actionInFlight === 'refund'}
            />
          )}
          <ActionButton
            label="Force cancel"
            icon={HiOutlineXCircle}
            danger
            onClick={() => {
              const reason = window.prompt('Cancellation reason:') || 'admin_force_cancel'
              if (window.confirm('Cancel this subscription immediately? This cannot be undone.')) {
                handleAction('cancel', () => superadminAPI.forceCancelSubscription(sub.id, reason))
              }
            }}
            disabled={actionInFlight === 'cancel'}
          />
        </div>
      </div>

      {/* Lifecycle events */}
      <div className="bg-white rounded-lg shadow-sm p-5">
        <h2 className="text-base font-semibold text-gray-900 mb-3">Lifecycle events ({events.length})</h2>
        <div className="divide-y divide-gray-100">
          {events.map(e => (
            <div key={e.id} className="py-2 text-sm flex items-center justify-between">
              <div>
                <span className="font-medium text-gray-900">{e.event_type}</span>
                {e.actor_name && <span className="text-gray-500 ml-2">by {e.actor_name}</span>}
              </div>
              <span className="text-xs text-gray-500">{new Date(e.created_at).toLocaleString()}</span>
            </div>
          ))}
          {events.length === 0 && <p className="text-sm text-gray-500 py-2">No events yet.</p>}
        </div>
      </div>

      {/* Stripe events */}
      <div className="bg-white rounded-lg shadow-sm p-5">
        <h2 className="text-base font-semibold text-gray-900 mb-3">Stripe events ({stripeEvents.length})</h2>
        <div className="divide-y divide-gray-100">
          {stripeEvents.map(e => (
            <div key={e.id} className="py-2 text-sm flex items-center justify-between">
              <div>
                <span className="font-medium text-gray-900">{e.event_type}</span>
                <code className="ml-2 text-xs text-gray-500 bg-gray-100 px-1 rounded">{e.stripe_event_id.slice(0, 16)}…</code>
              </div>
              <span className={`text-xs ${e.processed ? 'text-success' : 'text-warning'}`}>
                {e.processed ? 'processed' : 'pending'}
                {' · '}
                {new Date(e.received_at).toLocaleString()}
              </span>
            </div>
          ))}
          {stripeEvents.length === 0 && <p className="text-sm text-gray-500 py-2">No Stripe events yet.</p>}
        </div>
      </div>
    </div>
  )
}

const Field = ({ label, value, mono = false, highlight = false }) => (
  <div>
    <dt className="text-xs uppercase text-gray-500 tracking-wide">{label}</dt>
    <dd className={`mt-0.5 ${mono ? 'font-mono text-xs' : 'text-sm'} ${highlight ? 'font-bold text-warning' : 'text-gray-900'}`}>{value}</dd>
  </div>
)

const ActionButton = ({ label, icon: Icon, onClick, disabled, danger = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`px-3 py-2 text-sm font-medium rounded inline-flex items-center gap-1.5 disabled:opacity-50 ${
      danger
        ? 'border border-red-300 bg-white text-danger hover:bg-danger-soft'
        : 'border border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
    }`}
  >
    <Icon className="w-4 h-4" />
    {label}
  </button>
)
